import React, { useState, useEffect } from 'react'
import hospitalService from '../services/hospitalService'

function HospitalPartnershipRequests() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState(null)
  const [expandedRequest, setExpandedRequest] = useState(null)
  const [editingRequest, setEditingRequest] = useState(null)
  const [editFormData, setEditFormData] = useState({ request_message: '', expires_at: '' })
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    fetchPartnershipRequests()
  }, [])

  const fetchPartnershipRequests = async () => {
    try {
      setLoading(true)
      setError(null)
      // Use invitations API: GET /v1/hospital/partnerships/invitations?status=PENDING
      const response = await hospitalService.getPartnershipInvitations({ status: 'PENDING' })
      
      if (response && response.data) {
        setRequests(Array.isArray(response.data) ? response.data : [])
        if (response.pagination) {
          setPagination(response.pagination)
        }
      } else {
        setRequests([])
      }
    } catch (err) {
      console.error('Error fetching partnership requests:', err)
      setError(err.message || 'Failed to load partnership requests')
      setRequests([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (e) {
      return dateString
    }
  }

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffMinutes = Math.floor(diffMs / (1000 * 60))

      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
      return 'Just now'
    } catch (e) {
      return 'N/A'
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { 
        bg: 'bg-amber-50', 
        text: 'text-amber-700', 
        border: 'border-amber-200',
        dot: 'bg-amber-400'
      },
      APPROVED: { 
        bg: 'bg-emerald-50', 
        text: 'text-emerald-700', 
        border: 'border-emerald-200',
        dot: 'bg-emerald-400'
      },
      REJECTED: { 
        bg: 'bg-red-50', 
        text: 'text-red-700', 
        border: 'border-red-200',
        dot: 'bg-red-400'
      },
      ACCEPTED: { 
        bg: 'bg-blue-50', 
        text: 'text-blue-700', 
        border: 'border-blue-200',
        dot: 'bg-blue-400'
      },
    }
    
    const config = statusConfig[status] || { 
      bg: 'bg-gray-50', 
      text: 'text-gray-700', 
      border: 'border-gray-200',
      dot: 'bg-gray-400'
    }
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border ${config.bg} ${config.text} ${config.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
        {status || 'N/A'}
      </span>
    )
  }

  const getRequestType = (request) => {
    // Check if this is a new fleet registration based on requester_type or fleet_operator presence
    if (request.requester_type === 'FLEET' && !request.fleet_operator) {
      return { label: 'New Fleet Registration', icon: '🆕', color: 'text-blue-600' }
    }
    if (request.fleet_operator) {
      return { label: 'Existing Fleet Partnership', icon: '🤝', color: 'text-green-600' }
    }
    return { label: 'Partnership Invitation', icon: '📋', color: 'text-gray-600' }
  }

  const getFleetName = (request) => {
    // request_message is now a string, fleet_operator contains the fleet details
    if (request.fleet_operator && request.fleet_operator.name) {
      return request.fleet_operator.name
    }
    return 'Unknown Fleet'
  }

  const getFleetDetails = (request) => {
    // Use fleet_operator object from the response
    if (request.fleet_operator) {
      return {
        name: request.fleet_operator.name,
        city: request.fleet_operator.city,
        state: request.fleet_operator.state,
        contact_info: request.fleet_operator.contact_info,
        business_license: request.fleet_operator.business_license,
      }
    }
    return null
  }

  const toggleExpand = (requestId) => {
    setExpandedRequest(expandedRequest === requestId ? null : requestId)
  }

  const openEditModal = (request) => {
    // request_message is now a string in the new response structure
    const message = typeof request.request_message === 'string' 
      ? request.request_message 
      : request.request_message?.message || ''
    // Format expires_at for input field (YYYY-MM-DDTHH:mm format)
    let expiresAt = ''
    if (request.expires_at) {
      try {
        const date = new Date(request.expires_at)
        expiresAt = date.toISOString().slice(0, 16) // Format: YYYY-MM-DDTHH:mm
      } catch (e) {
        expiresAt = ''
      }
    }
    
    setEditingRequest(request)
    setEditFormData({
      request_message: message,
      expires_at: expiresAt
    })
  }

  const closeEditModal = () => {
    setEditingRequest(null)
    setEditFormData({ request_message: '', expires_at: '' })
  }

  const handleUpdateRequest = async () => {
    if (!editingRequest) return

    try {
      setUpdating(true)
      
      // Prepare update data
      const updateData = {}
      if (editFormData.request_message) {
        updateData.request_message = editFormData.request_message
      }
      if (editFormData.expires_at) {
        // Convert to ISO format with Z
        const date = new Date(editFormData.expires_at)
        updateData.expires_at = date.toISOString()
      }

      // Call the update API
      const response = await hospitalService.updatePartnershipRequest(
        editingRequest.request_id,
        updateData
      )

      // Update the request in the local state
      if (response && response.data) {
        setRequests(prevRequests =>
          prevRequests.map(req =>
            req.request_id === editingRequest.request_id
              ? { ...req, ...response.data }
              : req
          )
        )
      }

      // Close modal and refresh data
      closeEditModal()
      await fetchPartnershipRequests()
    } catch (err) {
      console.error('Error updating partnership request:', err)
      alert(err.message || 'Failed to update partnership request')
    } finally {
      setUpdating(false)
    }
  }

  const handleAcceptRequest = async (request) => {
    if (!request || !request.request_id) return

    // Confirm before accepting
    const confirmed = window.confirm(
      `Are you sure you want to accept this partnership invitation from ${getFleetName(request)}? This will create a partnership between your hospital and the fleet operator.`
    )

    if (!confirmed) return

    try {
      setUpdating(true)
      
      // Call the respond API with ACCEPTED status
      const response = await hospitalService.respondToPartnershipInvitation(
        request.request_id,
        'ACCEPTED'
      )

      // Remove the request from the list since status becomes FLEET_ACCEPTED
      // and it won't appear in PENDING status filter
      setRequests(prevRequests =>
        prevRequests.filter(req => req.request_id !== request.request_id)
      )

      // Show success message
      if (response && response.data) {
        const partnershipId = response.data.partnership_id
        const message = response.message || 'Partnership invitation accepted successfully'
        if (partnershipId) {
          alert(`${message}\nPartnership ID: ${partnershipId}`)
        } else {
          alert(message)
        }
      }

      // Refresh data to ensure consistency
      await fetchPartnershipRequests()
    } catch (err) {
      console.error('Error accepting partnership request:', err)
      alert(err.message || 'Failed to accept partnership request')
    } finally {
      setUpdating(false)
    }
  }

  const handleDeclineRequest = async (request) => {
    if (!request || !request.request_id) return

    // Confirm before deleting
    const confirmed = window.confirm(
      `Are you sure you want to decline this partnership request from ${getFleetName(request)}? This action will cancel the request and cannot be undone.`
    )

    if (!confirmed) return

    try {
      setUpdating(true)
      
      // Call the delete API (which cancels the request)
      const response = await hospitalService.deletePartnershipRequest(request.request_id)

      // Remove the request from the list since status becomes CANCELLED
      // and it won't appear in PENDING status filter
      setRequests(prevRequests =>
        prevRequests.filter(req => req.request_id !== request.request_id)
      )

      // Show success message
      if (response && response.message) {
        console.log('Request declined:', response.message)
      }

      // Refresh data to ensure consistency
      await fetchPartnershipRequests()
    } catch (err) {
      console.error('Error declining partnership request:', err)
      alert(err.message || 'Failed to decline partnership request')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="min-h-screen">
      <div className="p-6 max-w-9xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Partnership Requests</h1>
              <p className="text-gray-600">
                Review and manage partnership requests from fleet operators
              </p>
            </div>
            <button
              onClick={fetchPartnershipRequests}
              disabled={loading}
              className="px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          
          {pagination && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium text-blue-900">
                {pagination.total} {pagination.total === 1 ? 'request' : 'requests'} pending
              </span>
            </div>
          )}
      </div>

        {/* Loading State */}
        {loading && (
          <div className="rounded-2xl p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
            <div className="text-gray-600 font-medium">Loading partnership requests...</div>
            <div className="text-gray-500 text-sm mt-1">Please wait while we fetch the data</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-red-800 font-semibold mb-1">Error loading partnership requests</div>
                <div className="text-red-700 text-sm mb-4">{error}</div>
                <button
                  onClick={fetchPartnershipRequests}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-sm"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && requests.length === 0 && (
          <div className="rounded-2xl p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-gray-100/50 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Partnership Requests</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              There are no pending partnership requests at this time. New requests will appear here when fleet operators send partnership invitations.
            </p>
          </div>
        )}

        {/* Requests List */}
        {!loading && !error && requests.length > 0 && (
          <div className="space-y-4">
            {requests.map((request) => {
              const fleetDetails = getFleetDetails(request)
              const requestType = getRequestType(request)
              const fleetName = getFleetName(request)
              const isExpanded = expandedRequest === request.request_id

              return (
                <div 
                  key={request.request_id} 
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200 overflow-hidden"
                >
                  {/* Main Card Content */}
                  <div className="p-6">
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm">
                            {fleetName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">{fleetName}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-medium ${requestType.color}`}>
                                {requestType.icon} {requestType.label}
                              </span>
          </div>
          </div>
        </div>
                        
                        {/* Status Badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-4">
                          {getStatusBadge(request.request_status)}
                          {request.super_admin_status && (
                            <>
                              <span className="text-gray-300">•</span>
                              {getStatusBadge(request.super_admin_status)}
                              <span className="text-xs text-gray-500 font-medium">Super Admin Review</span>
                            </>
                          )}
                        </div>

                        {/* Message Preview */}
                        {request.request_message && (
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold text-gray-900">Message:</span> {
                                typeof request.request_message === 'string' 
                                  ? request.request_message 
                                  : request.request_message?.message || request.request_message
                              }
                            </p>
                          </div>
                        )}

                        {/* Timestamp */}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Created {getTimeAgo(request.created_at)}
                          </div>
                          {request.expires_at && (
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Expires {formatDate(request.expires_at)}
          </div>
                          )}
          </div>
        </div>
      </div>

                    {/* Fleet Details Section */}
                    {fleetDetails && (
                      <div className="mt-5 pt-5 border-t border-gray-200">
                        <button
                          onClick={() => toggleExpand(request.request_id)}
                          className="w-full flex items-center justify-between text-left mb-3 hover:text-blue-600 transition-colors"
                        >
                          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Fleet Details
                          </h4>
                          <svg 
                            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isExpanded && (
                          <div className="mt-4 p-5 bg-gray-50 border border-gray-200 rounded-xl">
                            <div className="grid md:grid-cols-2 gap-4">
                              {fleetDetails.name && (
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-500 mb-0.5">Name</div>
                                    <div className="text-sm font-semibold text-gray-900">{fleetDetails.name}</div>
                                  </div>
                                </div>
                              )}
                              {fleetDetails.email && (
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-500 mb-0.5">Email</div>
                                    <div className="text-sm text-gray-900">{fleetDetails.email}</div>
                                  </div>
                                </div>
                              )}
                              {(fleetDetails.phone || fleetDetails.contact_info) && (
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-500 mb-0.5">Contact</div>
                                    <div className="text-sm text-gray-900">{fleetDetails.phone || fleetDetails.contact_info}</div>
                                  </div>
                                </div>
                              )}
                              {fleetDetails.address && (
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-500 mb-0.5">Address</div>
                                    <div className="text-sm text-gray-900">{fleetDetails.address}</div>
                                  </div>
                                </div>
                              )}
                              {(fleetDetails.city || fleetDetails.state) && (
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-500 mb-0.5">Location</div>
                                    <div className="text-sm text-gray-900">
                                      {[fleetDetails.city, fleetDetails.state].filter(Boolean).join(', ')}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {fleetDetails.business_license && (
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-500 mb-0.5">Business License</div>
                                    <div className="text-sm text-gray-900">{fleetDetails.business_license}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 pt-5 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-xs text-gray-400 font-mono">
                        ID: {request.request_id.substring(0, 8)}...
                      </div>
                      <div className="flex gap-3">
                        {request.request_status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => openEditModal(request)}
                              className="px-5 py-2.5 bg-transparent border-2 border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => handleAcceptRequest(request)}
                              disabled={updating}
                              className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white text-sm font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updating ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Accepting...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Accept
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleDeclineRequest(request)}
                              disabled={updating}
                              className="px-5 py-2.5 bg-transparent border-2 border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updating ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  Declining...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Decline
                                </>
                              )}
                            </button>
                          </>
                        )}
                        {request.super_admin_status === 'PENDING' && (
                          <div className="px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                            <svg className="w-4 h-4 text-amber-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-amber-700">Awaiting Super Admin Approval</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* History Section */}
                    {request.history && request.history.length > 0 && isExpanded && (
                      <div className="mt-5 pt-5 border-t border-gray-200">
                        <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Activity History
                        </h5>
                        <div className="space-y-3">
                          {request.history.map((historyItem, idx) => (
                            <div 
                              key={historyItem.history_id || idx} 
                              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                              <div className="flex-1">
                                <div className="text-xs font-semibold text-gray-900 mb-1">
                                  {historyItem.change_type?.replace(/_/g, ' ')}
                                </div>
                                <div className="text-xs text-gray-600 mb-1">
                                  <span className="font-medium">{historyItem.old_status || 'N/A'}</span>
                                  <span className="mx-2">→</span>
                                  <span className="font-medium">{historyItem.new_status}</span>
                                </div>
                                {historyItem.change_reason && (
                                  <div className="text-xs text-gray-500 italic mt-1">
                                    {historyItem.change_reason}
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-1">
                                  {formatDate(historyItem.created_at)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <button
              disabled={pagination.page === 1}
              className="px-5 py-2.5 bg-transparent border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50/50 hover:border-gray-400 transition-all flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            <div className="px-6 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm font-semibold text-blue-900">
                Page {pagination.page} of {pagination.totalPages}
              </span>
            </div>
            <button
              disabled={pagination.page >= pagination.totalPages}
              className="px-5 py-2.5 bg-transparent border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50/50 hover:border-gray-400 transition-all flex items-center gap-2"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Edit Request Modal */}
        {editingRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Edit Partnership Request</h2>
                  <button
                    onClick={closeEditModal}
                    disabled={updating}
                    className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Update the request message and expiration date
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Request Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Message
                  </label>
                  <textarea
                    value={editFormData.request_message}
                    onChange={(e) => setEditFormData({ ...editFormData, request_message: e.target.value })}
                    placeholder="Enter your message for this partnership request..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={4}
                    disabled={updating}
                  />
                </div>

                {/* Expiration Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={editFormData.expires_at}
                    onChange={(e) => setEditFormData({ ...editFormData, expires_at: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={updating}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Select when this partnership request should expire
                  </p>
                </div>

                {/* Current Request Info */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Request Details</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div><span className="font-medium">Request ID:</span> {editingRequest.request_id}</div>
                    <div><span className="font-medium">Status:</span> {editingRequest.request_status}</div>
                    {editingRequest.created_at && (
                      <div><span className="font-medium">Created:</span> {formatDate(editingRequest.created_at)}</div>
                    )}
                  </div>
            </div>
          </div>

              <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={closeEditModal}
                  disabled={updating}
                  className="px-5 py-2.5 bg-transparent border-2 border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateRequest}
                  disabled={updating}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Update Request
                    </>
                  )}
                </button>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

export default HospitalPartnershipRequests
